import axios from 'axios';
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  clearLegacyTokenStorage,
} from '../utils/accessToken';
import { getConsentedAdClickIds, onNewAccountCreated } from './adClickIds';

clearLegacyTokenStorage();

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:4001/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

const applyAccessTokenHeader = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

export const rememberAccessToken = (token) => {
  const stored = setAccessToken(token);
  applyAccessTokenHeader(stored);
  return stored;
};

export const forgetAccessToken = () => {
  clearAccessToken();
  applyAccessTokenHeader(null);
};

let refreshInFlight = null;

/**
 * Rotate via the HttpOnly `budgit_rt` cookie (empty body). Mobile never
 * calls this — it still posts `{ refreshToken }` from SecureStore.
 */
export const refreshSession = async () => {
  if (!refreshInFlight) {
    refreshInFlight = api
      .post('/auth/refresh-token', {})
      .then((response) => {
        const next = response.data.accessToken || response.data.token;
        if (!next) {
          throw new Error('Refresh did not return an access token');
        }
        return rememberAccessToken(next);
      })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

const isNonRetryAuthUrl = (url = '') =>
  /\/auth\/(refresh-token|logout|generate-otp|verify-otp)/.test(String(url));

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response) {
      // Handle 401 Unauthorized — try cookie refresh once, then logout.
      if (error.response.status === 401) {
        const url = error.config?.url || '';
        if (isNonRetryAuthUrl(url)) {
          return Promise.reject(error);
        }
        if (error.config?._retry) {
          forgetAccessToken();
          window.dispatchEvent(new CustomEvent('auth:logout'));
          return Promise.reject(error);
        }
        error.config._retry = true;
        try {
          const next = await refreshSession();
          if (next) {
            error.config.headers = error.config.headers || {};
            error.config.headers.Authorization = `Bearer ${next}`;
            return api.request(error.config);
          }
        } catch (_) {
          // fall through to logout
        }
        forgetAccessToken();
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(error);
      }
      
      // Handle 403 - distinguish between admin access and PIN verification
      if (error.response.status === 403) {
        // Admin endpoint access denied - this is expected for non-admin users
        if (error.config?.url?.includes('/admin/')) {
          // Silently reject without logging to console
          return Promise.reject(error);
        }
        
        // PIN verification required
        if (error.response.data.requireVerification) {
          // Store current path for redirect after verification
          localStorage.setItem('returnUrl', window.location.pathname);
          
          // Get email from local storage
          const email = localStorage.getItem('userEmail');
          if (email) {
            // Dispatch event for PIN verification
            window.dispatchEvent(new CustomEvent('auth:requirePin', {
              detail: { email }
            }));
          }
        }
        
        return Promise.reject(error);
      }

      // Handle 402 Payment Required → trigger upgrade modal flow
      if (error.response.status === 402) {
        try {
          const detail = error.response.data || {};
          window.dispatchEvent(new CustomEvent('subscription:upgrade', { detail }));
        } catch (e) {
          // no-op
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Auth functions
export const generateOTP = async (payload) => {
  const response = await api.post('/auth/generate-otp', payload);
  return response.data;
};

export const verifyOTP = async (identifier, otp) => {
  try {
    console.log('verifyOTP: Starting verification', { identifier, otp });
    // Consented ad click ids ride along so the signup conversion carries
    // them (#2499); null without the analytics grant.
    const adClickIds = getConsentedAdClickIds();
    const response = await api.post('/auth/verify-otp', { ...identifier, otp, ...(adClickIds ? { adClickIds } : {}) });
    if (response.data?.user?.isNewUser) onNewAccountCreated();
    console.log('verifyOTP response:', {
      hasAccessToken: !!response.data.accessToken,
      hasRefreshToken: !!response.data.refreshToken,
      needsPinVerification: response.data.needsPinVerification,
      userIsPinSetup: response.data.user?.isPinSetup
    });

    if (response.data.accessToken) {
      const identifierValue = identifier.email || identifier.phone;
      console.log('verifyOTP: Setting auth state', {
        identifier: identifierValue,
        tokenLength: response.data.accessToken.length
      });
      
      // Store identifier for session
      if (identifier.email) {
        localStorage.setItem('userEmail', identifier.email);
      } else if (identifier.phone) {
        localStorage.setItem('userPhone', identifier.phone);
      }
      rememberAccessToken(response.data.accessToken);
      console.log('verifyOTP: Auth header set', {
        header: api.defaults.headers.common['Authorization']?.substring(0, 20) + '...'
      });
    }
    return {
      ...response.data,
      token: response.data.accessToken // For backward compatibility
    };
  } catch (error) {
    console.error('verifyOTP error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    if (error.response?.data?.needsPinVerification) {
      return error.response.data;
    }
    throw error;
  }
};

const getFriendlyDeviceName = () => {
  const userAgent = navigator.userAgent;
  
  // Get OS name
  let os = 'Unknown OS';
  if (userAgent.includes('Win')) os = 'Windows';
  else if (userAgent.includes('Mac')) os = 'macOS';
  else if (userAgent.includes('Linux')) os = 'Linux';
  else if (userAgent.includes('iPhone')) os = 'iOS';
  else if (userAgent.includes('Android')) os = 'Android';

  // Get browser name
  let browser = 'Unknown Browser';
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
  else if (userAgent.includes('Edg')) browser = 'Edge';

  // Get browser version
  const version = userAgent.match(/(Chrome|Firefox|Safari|Edge)\/(\d+)/)?.[2] || '';
  
  return `${os} (${browser} ${version})`;
};

export const verifyPIN = async (email, pin, isChangingPin = false) => {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No authentication token found. Please log in again.');
  }

  try {
    console.log('verifyPIN: Starting verification', {
      email,
      isChangingPin,
      hasInitialToken: !!token
    });

    const response = await api.post('/auth/verify-pin', { 
      email, 
      pin,
      deviceName: getFriendlyDeviceName(),
      isChangingPin
    });

    console.log('verifyPIN response:', {
      success: response.data.success,
      hasAccessToken: !!response.data.accessToken,
      hasRefreshToken: !!response.data.refreshToken
    });

    if (isChangingPin) {
      return { success: true };
    }

    // Use existing token if no new one provided
    const newToken = response.data.accessToken || token;
    
    console.log('verifyPIN: Setting auth state', {
      email,
      usingExistingToken: !response.data.accessToken,
      tokenLength: newToken.length
    });

    forgetAccessToken();

    // Set new auth state (access in memory; refresh stays on HttpOnly cookie)
    localStorage.setItem('userEmail', email);
    rememberAccessToken(newToken);

    // Dispatch auth event to trigger context update
    window.dispatchEvent(new CustomEvent('auth:login', {
      detail: { token: newToken, email }
    }));

    console.log('verifyPIN: Auth header set', {
      header: api.defaults.headers.common['Authorization']?.substring(0, 20) + '...'
    });
    
    return {
      ...response.data,
      success: true,
      token: newToken,
      email
    };
  } catch (error) {
    console.error('Error in verifyPIN:', error);

    // Handle specific error cases
    if (error.response?.status === 403) {
      // Incorrect PIN
      throw new Error('Incorrect PIN. Please try again.');
    } else if (error.response?.status === 401) {
      // Token expired or invalid during verification
      forgetAccessToken();
      localStorage.removeItem('userEmail');
      window.dispatchEvent(new CustomEvent('auth:logout'));
      throw new Error('Your session has expired. Please log in again.');
    } else if (error.response?.status === 429) {
      // Rate limiting
      throw new Error('Too many attempts. Please wait a moment before trying again.');
    } else if (error.response?.status >= 500) {
      // Server errors
      throw new Error('Server error. Please try again later.');
    } else if (!navigator.onLine) {
      // Network connectivity
      throw new Error('No internet connection. Please check your network.');
    }

    // Generic error case
    throw new Error('Failed to verify PIN. Please try again.');
  }
};

export const setupPIN = async (email, pin, currentPin = null) => {
  try {
    const response = await api.post('/auth/setup-pin', { 
      email, 
      pin,
      currentPin 
    });

    // Handle both old and new token formats. Refresh is HttpOnly cookie on web.
    const accessToken = response.data.token || response.data.accessToken;

    // Always update access token when provided (both initial setup AND PIN changes)
    // The backend returns new tokens with updated device fingerprints on PIN change
    if (accessToken) {
      rememberAccessToken(accessToken);
    } else if (!currentPin) {
      // Only require token on initial setup - PIN changes may not always return new tokens
      throw new Error('No access token received from server');
    }
    
    return {
      ...response.data,
      success: true,
      token: accessToken || response.data.accessToken // For backward compatibility
    };
  } catch (error) {
    console.error('Error in setupPIN:', error);
    throw error;
  }
};

export const checkPINSetup = async () => {
  const response = await api.get('/auth/check-pin-setup');
  return response.data;
};

// Account functions
export const getConnectedAccounts = async () => {
  try {
    const response = await api.get('/accounts');
    return response.data.accounts;
  } catch (error) {
    // Return empty array for family permission denied (children can't view accounts)
    if (error.response?.status === 403) {
      return [];
    }
    throw error;
  }
};

export const getTransactionsByAccount = async (accountId) => {
  const response = await api.get(`/transactions/${accountId}`);
  return response.data;
};

export const getTransactions = async (page = 1, limit = 30, filters = {}) => {
  const response = await api.get('/transactions', {
    params: {
      page: parseInt(page),
      limit: parseInt(limit),
      search: filters.search,
      showTransfers: String(filters.showTransfers || false),
      showIncome: String(filters.showIncome || false),
      showUncategorized: String(filters.showUncategorized || false),
      transferType: filters.transferType,
      startDate: filters.startDate,
      endDate: filters.endDate,
      minAmount: filters.minAmount,
      maxAmount: filters.maxAmount
    }
  });
  return response.data;
};

// Plaid functions
export const createLinkToken = async () => {
  const response = await api.post('/plaid/create_link_token');
  return response.data;
};

export const exchangePublicToken = async (public_token) => {
  const response = await api.post('/plaid/exchange_public_token', { public_token });
  return response.data;
};

export const removeAccount = async (accountId) => {
  const response = await api.delete(`/accounts/${accountId}`);
  return response.data;
};

export const removeItem = async (itemId) => {
  const response = await api.delete(`/plaid/items/${itemId}`);
  return response.data;
};

export const offboardUser = async ({ pin }) => {
  const response = await api.post('/users/offboard', { pin, confirm: true });
  return response.data;
};

/** #968 — download machine-readable export of user-owned data */
export const exportMyData = async () => {
  const response = await api.get('/users/me/export');
  return response.data;
};

/** #968 — schedule 14-day grace deletion */
export const requestAccountDeletion = async ({ pin }) => {
  const response = await api.post('/users/me/deletion-request', { pin, confirm: true });
  return response.data;
};

export const cancelAccountDeletion = async ({ pin }) => {
  const response = await api.post('/users/me/deletion-request/cancel', { pin });
  return response.data;
};

// Device management functions
export const getTrustedDevices = async () => {
  const response = await api.get('/auth/devices');
  return response.data;
};

export const removeTrustedDevice = async (fingerprint) => {
  const response = await api.delete(`/auth/devices/${fingerprint}`);
  return response.data;
};

// User management functions
export const updateUser = async (updates) => {
  const response = await api.patch('/users/me', updates);
  return response.data;
};

export const updateTransactionCategory = async (transactionId, category) => {
  const response = await api.patch(`/transactions/${transactionId}/category`, { category });
  return response.data;
};

// Family management
export const getFamily = async () => {
  const response = await api.get('/family');
  return response.data;
};

export const inviteFamilyMember = async ({ email, name, role, phone }) => {
  const response = await api.post('/family/invite', { email, name, role, phone });
  return response.data;
};

export const acceptFamilyInvitation = async (token) => {
  const response = await api.post('/family/accept', { token });
  return response.data;
};

export const removeFamilyMember = async (memberId) => {
  const response = await api.delete(`/family/members/${memberId}`);
  return response.data;
};

export const updateFamilyMember = async (memberId, payload = {}) => {
  const response = await api.put(`/family/members/${memberId}`, payload);
  return response.data;
};

export const assignFamilyAccount = async (memberId, params) => {
  const response = await api.post(`/family/members/${memberId}/assign-account`, params);
  return response.data;
};

export const assignBudgetToFamilyMember = async (budgetId, memberId) => {
  const response = await api.post(`/budgets/${budgetId}/assign-member`, { memberId });
  return response.data;
};

export const unassignBudgetFromFamilyMember = async (budgetId) => {
  const response = await api.delete(`/budgets/${budgetId}/assign-member`);
  return response.data;
};

export const assignBudgetCategoryToFamilyMember = async (budgetId, categoryId, memberId) => {
  const response = await api.post(`/budgets/${budgetId}/categories/${categoryId}/assign-member`, { memberId });
  return response.data;
};

export const unassignBudgetCategoryFromFamilyMember = async (budgetId, categoryId) => {
  const response = await api.delete(`/budgets/${budgetId}/categories/${categoryId}/assign-member`);
  return response.data;
};

// Budget ↔ Plaid account mappings — Issue #230
export const updateBudgetAccountMappings = async (budgetId, accountMappings) => {
  const response = await api.put(`/budgets/${budgetId}/account-mappings`, { accountMappings });
  return response.data;
};

// Reallocate every transaction in one budget category to another (preserves source) — Issue #150
export const reallocateBudgetCategoryTransactions = async (budgetId, sourceCategoryId, targetCategoryId) => {
  const response = await api.post(
    `/budgets/${budgetId}/categories/${sourceCategoryId}/reallocate`,
    { targetCategoryId }
  );
  return response.data;
};

export const getCategoryAssignSources = async (budgetId, categoryId) => {
  const response = await api.get(`/budgets/${budgetId}/categories/${categoryId}/assign-sources`);
  return response.data?.data;
};

export const assignCategoryMoney = async (budgetId, categoryId, amount, sourceId) => {
  const response = await api.post(`/budgets/${budgetId}/categories/${categoryId}/assign`, {
    amount,
    sourceId,
  });
  return response.data;
};

export const getFamilyMemberBudgets = async (memberId) => {
  const response = await api.get(`/family/members/${memberId}/budgets`);
  return response.data;
};

export const getUnattributedFamilyTransactions = async (options = {}) => {
  const response = await api.get('/transactions/family/unattributed', { params: options });
  return response.data;
};

export const attributeFamilyTransaction = async (transactionId, params) => {
  const response = await api.post(`/transactions/attribute/${transactionId}`, params);
  return response.data;
};

export const batchAttributeFamilyTransactions = async (params) => {
  const response = await api.post('/transactions/attribute/batch', params);
  return response.data;
};

export const claimAttributionAlert = async (alertId) => {
  const response = await api.post(`/alerts/${alertId}/claim-transaction`);
  return response.data;
};

export const disputeAttributionAlert = async (alertId) => {
  const response = await api.post(`/alerts/${alertId}/dispute-transaction`);
  return response.data;
};

export const confirmAttributionAlert = async (alertId, params = {}) => {
  const response = await api.post(`/alerts/${alertId}/confirm-transaction`, params);
  return response.data;
};

export const suggestAttributionAlert = async (alertId, params) => {
  const response = await api.post(`/alerts/${alertId}/suggest-user`, params);
  return response.data;
};

// Referral API functions
export const generateReferralCode = async () => {
  const response = await api.post('/referrals/generate');
  return response.data;
};

export const redeemReferralCode = async (code) => {
  const response = await api.post('/referrals/redeem', { code });
  return response.data;
};

export const getReferralStats = async () => {
  const response = await api.get('/referrals/stats');
  return response.data;
};

// Legal consent
export const recordLegalConsent = async ({ termsVersion, privacyVersion, plaidNotice } = {}) => {
  const response = await api.post('/legal/consent', { termsVersion, privacyVersion, plaidNotice });
  return response.data;
};

export const getConsentStatus = async () => {
  const response = await api.get('/legal/status');
  return response.data;
};

// Reimbursement transaction functions
export const getReimbursementTransactions = async (days = 90) => {
  const response = await api.get('/transactions/reimbursements', { params: { days } });
  return response.data;
};

export const getReimbursementRequests = async (days = 90) => {
  const response = await api.get('/transactions/reimbursements/requests', { params: { days } });
  return response.data;
};

export const getReimbursementRequestMatches = async (reimbursementTransactionId, days = 90) => {
  const response = await api.get(`/transactions/reimbursements/requests/${reimbursementTransactionId}/matches`, {
    params: { days }
  });
  return response.data;
};

export const handleReimbursementRequest = async (
  reimbursementTransactionId,
  action,
  transactionId,
  days = 90
) => {
  const response = await api.post(`/transactions/reimbursements/requests/${reimbursementTransactionId}`, {
    action,
    transactionId
  }, {
    params: { days }
  });
  return response.data;
};

export const linkReimbursement = async (transactionId, reimbursementTransactionId, amountCents) => {
  const response = await api.post(`/transactions/${transactionId}/link-reimbursement`, {
    reimbursementTransactionId,
    amountCents
  });
  return response.data;
};

// === Goals API ===

export const getGoals = async (params = {}) => {
  const response = await api.get('/goals', { params });
  return response.data;
};

export const getGoal = async (id) => {
  const response = await api.get(`/goals/${id}`);
  return response.data;
};

export const createGoal = async (goalData) => {
  const response = await api.post('/goals', goalData);
  return response.data;
};

export const updateGoal = async (id, updates) => {
  const response = await api.put(`/goals/${id}`, updates);
  return response.data;
};

export const deleteGoal = async (id) => {
  const response = await api.delete(`/goals/${id}`);
  return response.data;
};

export const getGoalsSummary = async () => {
  const response = await api.get('/goals/summary');
  return response.data;
};

export const updateGoalFunding = async (id, fundingCategories) => {
  const response = await api.put(`/goals/${id}/funding`, { fundingCategories });
  return response.data;
};

export const linkGoalAccount = async (id, linkedAccountIds) => {
  const response = await api.put(`/goals/${id}/link-account`, { linkedAccountIds });
  return response.data;
};

export const completeGoal = async (id) => {
  const response = await api.post(`/goals/${id}/complete`);
  return response.data;
};

export const recordGoalWithdrawal = async (id, { amount, reason, transactionId }) => {
  const response = await api.post(`/goals/${id}/record-withdrawal`, { amount, reason, transactionId });
  return response.data;
};

export const reconcileGoal = async (id) => {
  const response = await api.get(`/goals/${id}/reconcile`);
  return response.data;
};

// === Bills API ===

export const getBills = async (params = {}) => {
  const response = await api.get('/bills', { params });
  return response.data;
};

export const getBill = async (id) => {
  const response = await api.get(`/bills/${id}`);
  return response.data;
};

export const createBill = async (billData) => {
  const response = await api.post('/bills', billData);
  return response.data;
};

export const updateBill = async (id, updates) => {
  const response = await api.put(`/bills/${id}`, updates);
  return response.data;
};

export const deleteBill = async (id) => {
  const response = await api.delete(`/bills/${id}`);
  return response.data;
};

export const markBillPaid = async (id, options = {}) => {
  const response = await api.post(`/bills/${id}/pay`, options);
  return response.data;
};

export const getBillsSummary = async () => {
  const response = await api.get('/bills/summary');
  return response.data;
};

export const getBillsCalendar = async (year, month) => {
  const response = await api.get(`/bills/calendar/${year}/${month}`);
  return response.data;
};

/** POST /bills/parse — raster bill image as base64 (data URI ok; PNG/JPEG recommended). Server forwards to vision as JPEG; PDF not supported. Max 5MB raw. */
export const parseBill = async (billBase64) => {
  const response = await api.post('/bills/parse', { billBase64 });
  return response.data;
};

export const confirmBillMatch = async (billId, transactionId, alertId) => {
  const body = { transactionId };
  if (alertId) body.alertId = alertId;
  const response = await api.post(`/bills/${billId}/confirm-match`, body);
  return response.data;
};

export const rejectBillMatch = async (billId, transactionId, alertId) => {
  const body = { transactionId };
  if (alertId) body.alertId = alertId;
  const response = await api.post(`/bills/${billId}/reject-match`, body);
  return response.data;
};

// === Recurring Stream API ===

export const getRecurringStreams = async (params = {}) => {
  const response = await api.get('/bills/recurring', { params });
  return response.data;
};

export const getRecurringStreamReviewCandidates = async () => {
  const response = await api.get('/bills/recurring/review');
  return response.data;
};

export const approveRecurringStream = async (streamId, correctedPayee) => {
  const body = correctedPayee ? { correctedPayee } : {};
  const response = await api.post(`/bills/recurring/${streamId}/approve`, body);
  return response.data;
};

export const rejectRecurringStream = async (streamId) => {
  const response = await api.post(`/bills/recurring/${streamId}/reject`);
  return response.data;
};

export { api };
export default api;
